import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  getTrainingLog,
  getActivityLog,
  getDayPost,
  generateDateID,
} from "../../services/dayPostService";
import { sessionService } from "../../services/sessionService";
import {
  getLinkedSessionIDs,
  getPrimarySessionID,
} from "../../utils/sessionLinks";
import { isCoachAssignedSession } from "../../utils/sessionRules";
import DeleteTrainingLogDialog from "../../components/DayPost/training-log/DeleteTrainingLogDialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import { activityService } from "../../services/activityService";
import {
  TrainingLog,
  ActivityLog,
  Session,
  DayPost,
  ActivityMeasurement,
} from "../../types/post";
import { ActivityType } from "../../types/activity";
import { GlobalActivityService } from "../../services/globalActivityService";
import {
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowUpRightIcon,
  ArrowUpTrayIcon,
  ClockIcon,
  BoltIcon,
  HeartIcon,
  PencilIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  ShareIcon,
} from "@heroicons/react/24/outline";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/config";
import SummaryExportDialog from "../../components/dashboard/SummaryExportDialog";
import TrainingLogForm from "../../components/DayPost/training-log/TrainingLogForm";
import PerformanceFlow from "../../components/DayPost/performance/PerformanceFlow";
import PerformanceEventLink from "../../components/DayPost/performance/PerformanceEventLink";
import { getResultsForTrainingLog } from "../../services/dayPostService";
import SessionActivitiesTable from "../../components/sessions/SessionActivitiesTable";
import { SessionNotes } from "../../components/sessions/SessionNotesMarkdown";
import {
  useDayPostSubmission,
  usePendingDayPostOperations,
} from "../../hooks/useDayPostData";
import CreatePostDialog from "../../components/CreatePostDialog";
import { getUserPublicData } from "../../services/userService";
import { UserPublicData } from "../../types/user";
import { getPost } from "../../services/postService";
import peakLogo from "../../assets/peak-dark.png";
import { formatProviderAttribution, isGarminProvider } from "../../utils/providerAttribution";
import GarminAttribution from "../../components/GarminAttribution";

// Terra activity data interfaces
interface HeartRateSample {
  context?: number;
  timestamp?: string;
  timer_duration_seconds?: number | null;
  bpm?: number;
}

interface HeartRateZone {
  duration_seconds?: number;
  end_percentage?: number;
  zone?: number;
  name?: string;
  start_percentage?: number;
}

// Position data interfaces
interface PositionSample {
  timestamp: string;
  coords_lat_lng_deg: [number, number]; // [latitude, longitude] in degrees
  timer_duration_seconds: number;
}

interface PositionData {
  position_samples?: PositionSample[];
  center_pos_lat_lng_deg?: [number, number]; // [latitude, longitude] in degrees
  start_pos_lat_lng_deg?: [number, number]; // [latitude, longitude] in degrees
  end_pos_lat_lng_deg?: [number, number]; // [latitude, longitude] in degrees
}

// Power data interfaces
interface PowerSample {
  timestamp?: string;
  timer_duration_seconds?: number | null;
  watts?: number;
}

interface PowerData {
  avg_watts?: number;
  max_watts?: number;
  power_samples?: PowerSample[];
}

interface TerraActivityData {
  heart_rate_data?: {
    summary?: {
      avg_hrv_rmssd?: number | null;
      avg_hr_bpm?: number;
      resting_hr_bpm?: number | null;
      max_hr_bpm?: number;
      avg_hrv_sdnn?: number | null;
      user_max_hr_bpm?: number | null;
      min_hr_bpm?: number;
      hr_zone_data?: HeartRateZone[];
    };
    detailed?: {
      hrv_samples_sdnn?: any[];
      hrv_samples_rmssd?: any[];
      hr_samples?: HeartRateSample[];
    };
  };
  calories_data?: {
    total_burned_calories?: number;
    net_intake_calories?: number | null;
    net_activity_calories?: number;
    BMR_calories?: number | null;
  };
  work_data?: {
    work_kilojoules?: number;
  };
  metadata?: {
    name?: string;
    start_time?: string;
    end_time?: string;
  };
  user?: {
    provider?: string;
    deviceModel?: string;
  };
  position_data?: PositionData;
  power_data?: PowerData;
}

const TrainingLogInsights: React.FC = () => {
  const { trainingLogId } = useParams<{ trainingLogId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [trainingLog, setTrainingLog] = useState<TrainingLog | null>(null);
  const [activityLog, setActivityLog] = useState<ActivityLog | null>(null);
  // Every session linked to the log (plan 3a), primary first. `session` is
  // the primary and feeds the wearable chart markers as before.
  const [linkedSessions, setLinkedSessions] = useState<
    (Session & { id: string })[]
  >([]);
  const session: Session | null = linkedSessions[0] ?? null;
  const [unlinkingSession, setUnlinkingSession] = useState<
    (Session & { id: string }) | null
  >(null);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [globalActivityTypes, setGlobalActivityTypes] = useState<
    Record<string, ActivityType[]>
  >({});
  const [terraActivityData, setTerraActivityData] =
    useState<TerraActivityData | null>(null);
  const [dayPost, setDayPost] = useState<DayPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  // State for editing
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // State for section toggles - only one can be active at a time
  const [activeSection, setActiveSection] = useState<
    "load" | "activities" | "wearable" | null
  >(null);

  // State for post creation
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [userData, setUserData] = useState<UserPublicData | null>(null);
  const [postImageUrl, setPostImageUrl] = useState<string | null>(null);
  const [postExists, setPostExists] = useState(false);
  const [radialPlaybackProgress, setRadialPlaybackProgress] = useState(1);
  const [isRadialPlaying, setIsRadialPlaying] = useState(false);
  const radialPlaybackRafRef = useRef<number | null>(null);

  const startRadialPlayback = useCallback(() => {
    if (radialPlaybackRafRef.current !== null) {
      cancelAnimationFrame(radialPlaybackRafRef.current);
      radialPlaybackRafRef.current = null;
    }

    setIsRadialPlaying(true);
    setRadialPlaybackProgress(0);

    const animationDurationMs = 10000;
    let startTimestamp: number | null = null;

    const animate = (timestamp: number) => {
      if (startTimestamp === null) {
        startTimestamp = timestamp;
      }

      const elapsed = timestamp - startTimestamp;
      const progress = Math.min(elapsed / animationDurationMs, 1);
      setRadialPlaybackProgress(progress);

      if (progress < 1) {
        radialPlaybackRafRef.current = requestAnimationFrame(animate);
      } else {
        setIsRadialPlaying(false);
        radialPlaybackRafRef.current = null;
      }
    };

    radialPlaybackRafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    return () => {
      if (radialPlaybackRafRef.current !== null) {
        cancelAnimationFrame(radialPlaybackRafRef.current);
      }
    };
  }, []);

  // Use submission hook for editing/deleting
  const { deleteTrainingLogById } = useDayPostSubmission(
    async () => {
      // Refresh data after successful operation
      await fetchData();
    },
  );
  const { addPendingItem } = usePendingDayPostOperations(async () => {
    await fetchData();
  });

  // Function to fetch Terra activity data
  const fetchTerraActivityData = useCallback(async (activityLogId: string) => {
    try {
      const getUserActivityData = httpsCallable(
        functions,
        "getUserActivityData",
      );
      const result = await getUserActivityData({
        activityLogID: activityLogId,
      });

      const responseData = result.data as {
        success: boolean;
        data?: TerraActivityData;
        error?: string;
      };

      if (responseData.success && responseData.data) {
        setTerraActivityData(responseData.data);
      } else {
        console.warn("No Terra activity data available:", responseData.error);
      }
    } catch (err) {
      console.error("Error fetching Terra activity data:", err);
      // Don't set error state for Terra data as it's optional
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!currentUser?.uid || !trainingLogId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch user data and training log in parallel
      const [training, userDataResult] = await Promise.all([
        getTrainingLog(currentUser.uid, trainingLogId),
        getUserPublicData(currentUser.uid),
      ]);

      setTrainingLog(training);
      setUserData(userDataResult);

      // Check if linked post still exists and fetch its media
      if (training?.linkedPostID && currentUser.uid) {
        const post = await getPost(currentUser.uid, training.linkedPostID);
        if (post) {
          setPostExists(true);
          setPostImageUrl(post.mediaList?.[0] || null);
        } else {
          // Post was deleted from the feed
          setPostExists(false);
          setPostImageUrl(null);
        }
      } else {
        setPostExists(false);
        setPostImageUrl(null);
      }

      // Fetch every linked session (primary first). Missing docs are skipped.
      const linkedIDs = getLinkedSessionIDs(training);
      if (linkedIDs.length > 0) {
        try {
          const sessionDocs = await Promise.all(
            linkedIDs.map(async (id) => {
              try {
                const data = await sessionService.getSession(
                  currentUser.uid,
                  id,
                );
                return data ? { ...data, id } : null;
              } catch (err) {
                console.error(`Error fetching linked session ${id}:`, err);
                return null;
              }
            }),
          );
          setLinkedSessions(
            sessionDocs.filter(
              (s): s is Session & { id: string } => s !== null,
            ),
          );

          // Also fetch activity types to display session measurements properly
          const activityTypesData = await activityService.fetchActivityTypes(
            currentUser.uid,
          );
          setActivityTypes(activityTypesData);
        } catch (sessionError) {
          console.error("Error fetching session data:", sessionError);
          // Continue without session data if fetch fails
        }
      } else {
        setLinkedSessions([]);
      }

      // Fetch day post for peak score
      if (training?.timestamp) {
        try {
          const dateID = generateDateID(new Date(training.timestamp));
          const dayPostData = await getDayPost(currentUser.uid, dateID);
          setDayPost(dayPostData);
        } catch (err) {
          console.warn("Could not fetch day post for peak score:", err);
        }
      }

      // Fetch linked activity if exists
      if (training?.linkedActivityLogID) {
        const activity = await getActivityLog(
          currentUser.uid,
          training.linkedActivityLogID,
        );
        setActivityLog(activity);

        // Prefer cached data on the ActivityLog; otherwise fetch via callable
        if (activity) {
          const hasCachedPower =
            activity.powerSamples ||
            activity.avgWatts != null ||
            activity.maxWatts != null;

          if (activity.heartRateData || activity.positionData || hasCachedPower) {
            setTerraActivityData({
              heart_rate_data: activity.heartRateData,
              position_data: activity.positionData,
              power_data: hasCachedPower
                ? {
                    avg_watts: activity.avgWatts,
                    max_watts: activity.maxWatts,
                    power_samples: activity.powerSamples,
                  }
                : undefined,
              metadata: {
                start_time: activity.startTime,
                end_time: activity.endTime,
                name: activity.sessionName,
              },
              user: {
                provider: activity.provider,
                deviceModel: activity.deviceModel,
              },
            });
          } else {
            // Fallback to fetching full Terra activity data
            fetchTerraActivityData(training.linkedActivityLogID);
          }
        }
      }
    } catch (err) {
      console.error("Error fetching training log data:", err);
      setError("Failed to load training log data");
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid, trainingLogId, fetchTerraActivityData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return hours > 0 ? `${hours}hr ${mins}m` : `${mins}m`;
  };

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Format a YYYYMMDD dateID (the day the log is for) in local time. This is the
  // training day, which can differ from when the log was recorded (timestamp).
  const formatDateID = (dateID: string): string => {
    const year = parseInt(dateID.substring(0, 4), 10);
    const month = parseInt(dateID.substring(4, 6), 10) - 1;
    const day = parseInt(dateID.substring(6, 8), 10);
    return new Date(year, month, day).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatSessionDuration = (
    startTime: string,
    endTime: string,
  ): string => {
    try {
      const start = new Date(startTime).getTime();
      const end = new Date(endTime).getTime();
      const durationMs = end - start;
      const totalMinutes = Math.round(durationMs / (1000 * 60));

      if (totalMinutes < 60) {
        return `${totalMinutes} min`;
      } else {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
      }
    } catch (error) {
      return "Unknown";
    }
  };

  const getRawActivityId = (activityTypeId: string): string => {
    if (activityTypeId.startsWith("global_")) {
      const parts = activityTypeId.split("_");
      if (parts.length >= 3) {
        return parts.slice(2).join("_");
      }
    }
    return activityTypeId;
  };

  const getActivityTypeName = (
    activity: string | ActivityMeasurement,
  ): string => {
    const activityTypeId =
      typeof activity === "string" ? activity : activity.ActivityTypeID;
    const rawActivityId = getRawActivityId(activityTypeId);

    const userActivityType = activityTypes.find(
      (type) => type.id === rawActivityId,
    );
    if (userActivityType) return userActivityType.name;

    if (typeof activity !== "string" && activity.globalCategoryID) {
      const categoryTypes =
        globalActivityTypes[activity.globalCategoryID] || [];
      const packActivityType = categoryTypes.find(
        (type) => type.id === rawActivityId,
      );
      if (packActivityType) return packActivityType.name;
    }

    return "Unknown Activity";
  };

  useEffect(() => {
    const allMeasurements = linkedSessions.flatMap(
      (s) => s.ActivityMeasurementList ?? [],
    );
    if (allMeasurements.length === 0) return;

    const categoryIds = Array.from(
      new Set(
        allMeasurements
          .map((m) => m.globalCategoryID)
          .filter((categoryId): categoryId is string => !!categoryId),
      ),
    );

    if (categoryIds.length === 0) return;

    const fetchGlobalTypes = async () => {
      try {
        const entries = await Promise.all(
          categoryIds.map(async (categoryId) => {
            const types =
              await GlobalActivityService.fetchActivityTypes(categoryId);
            const normalized: ActivityType[] = types.map((type) => ({
              id: type.id,
              name: type.name,
              description: type.description,
              variables: type.variables,
              image: type.image,
              isFromPack: true,
              categoryId: type.categoryId,
            }));
            return [categoryId, normalized] as const;
          }),
        );

        setGlobalActivityTypes((prev) => ({
          ...prev,
          ...Object.fromEntries(entries),
        }));
      } catch (error) {
        console.error(
          "Error fetching global activity types for insights:",
          error,
        );
      }
    };

    fetchGlobalTypes();
  }, [linkedSessions]);

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  // Handle edit training log
  const handleEdit = useCallback(() => {
    setShowActionsMenu(false);
    setShowEditForm(true);
  }, []);

  // Handle delete training log. Performance logs count their linked results
  // so the dialog can ask "Also delete N linked results?".
  const [linkedResultCount, setLinkedResultCount] = useState(0);
  const handleDelete = useCallback(() => {
    setShowActionsMenu(false);
    setLinkedResultCount(0);
    if (trainingLog?.performanceType && currentUser?.uid && trainingLogId) {
      getResultsForTrainingLog(currentUser.uid, trainingLogId)
        .then((results) => setLinkedResultCount(results.length))
        .catch(() => {});
    }
    setShowDeleteConfirm(true);
  }, [trainingLog?.performanceType, currentUser?.uid, trainingLogId]);

  // Handle confirm delete. `deleteLinkedSessionsAndActivity` is the athlete's
  // answer to "Delete the linked sessions and activity data too?" (decision 5).
  const handleConfirmDelete = useCallback(
    async (
      deleteLinkedSessionsAndActivity: boolean,
      deleteLinkedResults: boolean = false,
    ) => {
      if (!currentUser?.uid || !trainingLogId || !trainingLog) return;

      try {
        setIsDeleting(true);
        // The log's own day (dateID) is authoritative; fall back to the
        // timestamp for legacy logs without one.
        const dateID =
          trainingLog.dateID ||
          generateDateID(new Date(trainingLog.timestamp));

        await deleteTrainingLogById(
          trainingLogId,
          dateID,
          deleteLinkedSessionsAndActivity,
          addPendingItem,
          deleteLinkedResults,
        );

        // Navigate back after successful deletion
        navigate(-1);
      } catch (error) {
        console.error("Error deleting training log:", error);
        setError("Failed to delete training log");
      } finally {
        setIsDeleting(false);
        setShowDeleteConfirm(false);
      }
    },
    [currentUser?.uid, trainingLogId, trainingLog, deleteTrainingLogById, addPendingItem, navigate],
  );

  // Unlink one session from this log (plan 3a). The session stays complete
  // and reappears under "Sessions to log".
  const handleConfirmUnlink = useCallback(async () => {
    if (!currentUser?.uid || !trainingLogId || !unlinkingSession) return;
    try {
      setIsUnlinking(true);
      await sessionService.unlinkSessionFromLog(
        currentUser.uid,
        unlinkingSession.id,
        trainingLogId,
      );
      setUnlinkingSession(null);
      await fetchData();
    } catch (error) {
      console.error("Error unlinking session:", error);
      setError("Failed to unlink session");
    } finally {
      setIsUnlinking(false);
    }
  }, [currentUser?.uid, trainingLogId, unlinkingSession, fetchData]);

  // Handle training form submit
  // Handle close edit form
  const handleCloseEditForm = useCallback(() => {
    setShowEditForm(false);
  }, []);

  // Close actions menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".actions-menu")) {
        setShowActionsMenu(false);
      }
    };

    if (showActionsMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showActionsMenu]);

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="loading loading-spinner text-base-content/60 loading-lg"></div>
          <p className="mt-4 text-base-content/70 font-grotesk font-bold">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (error || !trainingLog) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500">{error || "Training log not found"}</p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-base-2000 text-white rounded-md hover:bg-base-content/60"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20 sm:pb-4">
      <div className="mx-auto">
        {/* Header */}
        <div className="mb-4 py-8 bg-base-100">
          <div className="flex items-center justify-between max-w-4xl mx-auto px-4">
            <div className="flex justify-between items-center space-x-3">
              <button
                onClick={handleBack}
                className="p-1 rounded-full hover:bg-base-300 bg-base-300 transition-colors"
              >
                <ChevronLeftIcon className="h-8 w-8 text-base-content/70" />
              </button>
              <div>
                <h1 className="text-xl font-grotesk font-light text-base-content">
                  <span className="font-bold">Training</span> Log
                </h1>
                <p className="text-sm text-base-content/70">
                  {trainingLog.dateID
                    ? formatDateID(trainingLog.dateID)
                    : formatDate(trainingLog.timestamp)}
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              {/* Export Button */}
              <button
                onClick={() => setExportOpen(true)}
                className="p-2 hover:bg-base-200 transition-colors bg-base-300 rounded-full"
              >
                <ArrowUpTrayIcon className="h-5 w-5 text-base-content/70" />
              </button>

              {/* Actions Menu */}
              <div className="relative actions-menu">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className="p-2 rounded-md hover:bg-base-200 transition-colors"
                >
                  <EllipsisVerticalIcon className="h-5 w-5 text-base-content/70" />
                </button>

                {showActionsMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-base-100 rounded-md shadow-lg border border-base-300 z-10">
                    <div className="py-1">
                      <button
                        onClick={handleEdit}
                        className="flex items-center w-full px-4 py-2 text-sm text-base-content/80 hover:bg-base-200 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4 mr-3" />
                        Edit
                      </button>
                      <button
                        onClick={handleDelete}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-500/10 transition-colors"
                      >
                        <TrashIcon className="h-4 w-4 mr-3" />
                        Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Training Log Section */}
        <div className=" mb-4 px-4 max-w-4xl mx-auto">
          {/* Shared as Post Badge */}
          {postExists && trainingLog.linkedPostID && (
            <div className="mb-4 flex items-center justify-between bg-blue-500/10 border border-blue-500 rounded-lg p-3 mx-4">
              <div className="flex items-center space-x-3">
                {postImageUrl && (
                  <img
                    src={postImageUrl}
                    alt="Shared post"
                    className="w-12 h-12 object-cover rounded-md"
                  />
                )}
                <div className="flex justify-between items-center">
                  <p className="font-medium text-blue-500 text-sm">
                    Shared as Post
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Event link (Events.md §3.4) — performances only */}
          {trainingLog.performanceType &&
            trainingLogId &&
            currentUser?.uid && (
              <div className="mb-3">
                <PerformanceEventLink
                  userId={currentUser.uid}
                  trainingLogId={trainingLogId}
                  dateID={trainingLog.dateID}
                  eventId={trainingLog.eventId}
                  onChanged={fetchData}
                />
              </div>
            )}

          {/* Notes */}
          {trainingLog.notes && trainingLog.notes.length > 0 && (
            <div className="p-4 bg-base-100 mx-4 rounded-md shadow-sm">
              <div className="">
                <p className="font-light text-xs font-geist">Notes</p>
                <p className="text-lg font-sans text-base-content whitespace-pre-wrap mt-1">
                  {trainingLog.notes}
                </p>
                {/* Tags */}
                {trainingLog.tag && trainingLog.tag.length > 0 && (
                  <div className="mt-2">
                    <div className="flex flex-wrap gap-1">
                      {trainingLog.tag.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-block bg-base-300 text-base-content px-2 py-1 rounded-sm text-sm font-grotesk font-bold"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Training Log Section */}
          <div className="p-4 pb-0 pt-4">
            {/* Load Section Container - Hide in casual mode */}
            {!userData?.casualMode && (
              <div
                className={`transition-all ${
                  activeSection === "load"
                    ? "bg-base-100 rounded-md pb-4"
                    : "bg-base-100 rounded-md pb-4"
                }`}
              >
                <div
                  onClick={() =>
                    setActiveSection(activeSection === "load" ? null : "load")
                  }
                  className="w-full flex items-center justify-between p-4 transition-colors cursor-pointer pb-0"
                >
                  <h2 className="text-lg text-base-content font-grotesk">
                    Load
                  </h2>
                  <div className="flex items-center space-x-2 text-sm text-base-content/70">
                    {activeSection === "load" ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </div>
                </div>

                {/* Training Log Content */}
                {activeSection === "load" && (
                  <>
                    {/* Key Metrics */}
                    <div className="space-y-4 px-4 pt-4">
                      <div className="flex justify-between items-center bg-base-200 p-4 rounded-md">
                        <div className="text-sm text-base-content/70">Load</div>
                        <div className="text-lg font-semibold font-sans text-base-content">
                          {Math.round(trainingLog.load)}
                        </div>
                      </div>
                      <div className="flex justify-between items-center bg-base-200 p-4 rounded-md">
                        <div className="text-sm text-base-content/70">
                          Duration
                        </div>
                        <div className="text-lg font-semibold font-sans text-base-content">
                          {formatDuration(trainingLog.duration)}
                        </div>
                      </div>
                      <div className="flex justify-between items-center bg-base-200 p-4 rounded-md">
                        <div className="text-sm text-base-content/70">RPE</div>
                        <div className="text-lg font-semibold font-sans text-base-content">
                          {trainingLog.rpe.toFixed(1)}
                        </div>
                      </div>
                    </div>

                    {/* Effort Breakdown */}
                    <div className="mb-4 p-4 pt-4 pb-6 mt-4 bg-base-200 rounded-md mx-4">
                      <h3 className="text-md text-base-content mb-2">
                        Perceived Effort (RPE)
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-1 items-center">
                            <span className="text-base-content/70">
                              Muscular
                            </span>
                            <span className="text-base-content text-md">
                              <strong>{trainingLog.muscular}</strong> / 10
                            </span>
                          </div>
                          <div className="w-full bg-base-300 rounded-full h-1.5">
                            <div
                              className="bg-base-content/60 h-1.5 rounded-full"
                              style={{
                                width: `${(trainingLog.muscular / 10) * 100}%`,
                              }}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1 items-center">
                            <span className="text-base-content/70">
                              Respiratory
                            </span>
                            <span className="text-base-content text-md">
                              <strong>{trainingLog.respiratory}</strong> / 10
                            </span>
                          </div>
                          <div className="w-full bg-base-300 rounded-full h-1.5">
                            <div
                              className="bg-base-content/60 h-1.5 rounded-full"
                              style={{
                                width: `${
                                  (trainingLog.respiratory / 10) * 100
                                }%`,
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Performance Rating - Only show if available */}
                    {trainingLog.trainingPerformed !== undefined && (
                      <div className="mb-4 p-4 pt-4 pb-6 mt-4 bg-base-200 rounded-md mx-4">
                        <h3 className="text-md text-base-content mb-2">
                          Performance Rating
                        </h3>
                        <div>
                          <div className="flex justify-between text-sm mb-1 items-center">
                            <span className="text-base-content/70">
                              Performance Rating
                            </span>
                            <span className="text-base-content text-md">
                              <strong>{trainingLog.trainingPerformed}</strong> /
                              10
                            </span>
                          </div>
                          <div>
                            <div className="w-full bg-base-300 rounded-full h-1.5">
                              <div
                                className="bg-base-content/60 h-1.5 rounded-full"
                                style={{
                                  width: `${
                                    (trainingLog.trainingPerformed / 10) * 100
                                  }%`,
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show message when performance rating is not available */}
                    {trainingLog.trainingPerformed === undefined &&
                      trainingLog.rpe < 7.5 && (
                        <div className="p-4 rounded-lg mb-4 bg-base-200 mx-4">
                          <h3 className="text-md text-base-content">
                            Performance Rating
                          </h3>
                          <div className="text-xs text-base-content/60 italic">
                            Performance rating not recorded (RPE was below 7.5)
                          </div>
                        </div>
                      )}

                    {trainingLog.trainingPerformed === undefined &&
                      trainingLog.rpe >= 7.5 && (
                        <div className="pl-4">
                          <h3 className="text-md font-medium text-base-content mb-2">
                            Performance Rating
                          </h3>
                          <div className="text-sm text-base-content/60 italic">
                            Performance rating not available
                          </div>
                        </div>
                      )}
                  </>
                )}
              </div>
            )}
          </div>
          {/* Linked Session Section */}
          {session && (
            <div className="p-4 px-4 pb-0">
              {/* Session Activities Container */}
              <div
                className={`transition-all ${
                  activeSection === "activities"
                    ? "bg-base-100 rounded-md "
                    : "bg-base-100 rounded-md "
                }`}
              >
                <div
                  onClick={() =>
                    setActiveSection(
                      activeSection === "activities" ? null : "activities",
                    )
                  }
                  className="w-full flex items-center justify-between p-4 transition-colors cursor-pointer"
                >
                  <h2 className="text-lg text-base-content font-grotesk">
                    Session Activities
                    {linkedSessions.length > 1 && (
                      <span className="ml-2 text-sm font-geist text-base-content/60">
                        {linkedSessions.length} sessions
                      </span>
                    )}
                  </h2>
                  <div className="flex items-center space-x-2 text-sm text-base-content/70">
                    {activeSection === "activities" ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </div>
                </div>

                {/* One activities section per linked session (plan 3a):
                    header with title, time and Unlink, then coach notes and
                    the measurements table. */}
                {activeSection === "activities" && (
                  <div className="px-4 pb-4 space-y-3">
                    {linkedSessions.map((linked, index) => {
                      const measurements = linked.ActivityMeasurementList ?? [];
                      const hasNotes = (linked.activityBlocks || []).some(
                        (b) =>
                          b.blockType === "notes" &&
                          (b.notesContent || "").trim(),
                      );
                      const title =
                        linked.name?.trim() ||
                        (linkedSessions.length > 1
                          ? `Session ${index + 1}`
                          : "Session");
                      const isPrimary = linkedSessions.length > 1 && index === 0;
                      return (
                        <div
                          key={linked.id}
                          className="rounded-xl border border-base-300 bg-base-200/40"
                        >
                          <div className="flex items-start justify-between gap-3 p-3">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(`/session/${linked.id}`)
                                  }
                                  className="text-sm font-heading text-base-content truncate hover:underline text-left"
                                >
                                  {title}
                                </button>
                                {isPrimary && (
                                  <span className="text-[10px] uppercase tracking-wider font-grotesk text-base-content/60 bg-base-100 border border-base-300 rounded-md px-1.5 py-0.5">
                                    Primary
                                  </span>
                                )}
                                {isCoachAssignedSession(linked) && (
                                  <span className="text-[10px] uppercase tracking-wider font-grotesk text-base-content/60">
                                    Coach
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-base-content/50 font-geist tabular-nums mt-0.5">
                                <ClockIcon className="h-3 w-3 flex-shrink-0" />
                                <span>
                                  {linked.startTime
                                    ? new Date(linked.startTime).toLocaleString(
                                        "en-US",
                                        {
                                          month: "short",
                                          day: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        },
                                      )
                                    : linked.plannedDate}
                                  {linked.startTime && linked.endTime && (
                                    <>
                                      {" "}
                                      –{" "}
                                      {new Date(linked.endTime).toLocaleTimeString(
                                        "en-US",
                                        { hour: "2-digit", minute: "2-digit" },
                                      )}{" "}
                                      ·{" "}
                                      {formatSessionDuration(
                                        linked.startTime,
                                        linked.endTime,
                                      )}
                                    </>
                                  )}
                                  {" · "}
                                  {measurements.length}{" "}
                                  {measurements.length === 1
                                    ? "activity"
                                    : "activities"}
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setUnlinkingSession(linked)}
                              className="flex-shrink-0 text-xs font-grotesk text-base-content/60 hover:text-error border border-base-300 hover:border-error/40 rounded-lg px-2.5 py-1.5 transition-colors"
                            >
                              Unlink
                            </button>
                          </div>
                          {(measurements.length > 0 || hasNotes) && (
                            <div className="px-3 pb-3 space-y-3">
                              <SessionNotes blocks={linked.activityBlocks} />
                              {measurements.length > 0 && (
                                <SessionActivitiesTable
                                  measurements={measurements}
                                  getActivityName={getActivityTypeName}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Linked Activity Section */}
          {activityLog && (
            <div className="p-4 ">
              {/* Wearable Device Data Container */}
              <div
                className={`transition-all ${
                  activeSection === "wearable"
                    ? "bg-base-100 rounded-md "
                    : "bg-base-100 rounded-md "
                }`}
              >
                <div
                  onClick={() =>
                    setActiveSection(
                      activeSection === "wearable" ? null : "wearable",
                    )
                  }
                  className="w-full flex items-center justify-between p-4 transition-colors cursor-pointer"
                >
                  <div className="flex items-center space-x-3">
                    <h2 className="text-lg text-base-content font-grotesk">
                      Wearable Device Data
                    </h2>
                    {isGarminProvider(activityLog.provider) ? (
                      <GarminAttribution deviceModel={activityLog.deviceModel} />
                    ) : formatProviderAttribution(activityLog.provider, activityLog.deviceModel) && (
                      <span className="text-xs bg-base-300 text-base-content/80 px-2 py-1 rounded">
                        {formatProviderAttribution(activityLog.provider, activityLog.deviceModel)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-base-content/70">
                    {activeSection === "wearable" ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </div>
                </div>

                {/* Wearable Data Content */}
                {activeSection === "wearable" && (
                  <>
                    <div className="flex space-x-4 px-4">
                      {activityLog.duration && (
                        <div className="text-center bg-base-200 p-4 rounded-md">
                          <div className="text-xs text-base-content/60">
                            Duration
                          </div>
                          <div className="text-lg font-bold text-base-content font-sans">
                            {formatDuration(activityLog.duration)}
                          </div>
                        </div>
                      )}
                      {activityLog.aveHR && (
                        <div className="text-center bg-base-200 p-4 rounded-md">
                          <div className="text-xs text-base-content/60">
                            Avg HR
                          </div>
                          <div className="text-lg font-bold text-base-content font-sans">
                            {Math.round(activityLog.aveHR)}
                          </div>
                        </div>
                      )}
                      {activityLog.maxHR && (
                        <div className="text-center bg-base-200 p-4 rounded-md">
                          <div className="text-xs text-base-content/60">
                            Max HR
                          </div>
                          <div className="text-lg font-bold  text-base-content font-sans">
                            {Math.round(activityLog.maxHR)}
                          </div>
                        </div>
                      )}
                      {activityLog.avgWatts && (
                        <div className="text-center bg-base-200 p-4 rounded-md">
                          <div className="text-xs text-base-content/60">
                            Avg Power
                          </div>
                          <div className="text-lg font-bold text-base-content font-sans">
                            {Math.round(activityLog.avgWatts)}W
                          </div>
                        </div>
                      )}
                      {activityLog.maxWatts && (
                        <div className="text-center bg-base-200 p-4 rounded-md">
                          <div className="text-xs text-base-content/60">
                            Max Power
                          </div>
                          <div className="text-lg font-bold text-base-content font-sans">
                            {Math.round(activityLog.maxWatts)}W
                          </div>
                        </div>
                      )}
                    </div>

                    {/* GPS Route Map Section */}
                    {terraActivityData?.position_data?.position_samples &&
                      terraActivityData.position_data.position_samples.length >
                        0 && (
                        <div className="bg-base-200 shadow-sm mt-4 mb-4 rounded-md mx-4">
                          <div className="flex items-center space-x-2 p-4">
                            <h2 className="text-base text-base-content">
                              Route
                            </h2>
                            {!isGarminProvider(terraActivityData.user?.provider) && formatProviderAttribution(terraActivityData.user?.provider, terraActivityData.user?.deviceModel) && (
                              <span className="text-xs bg-base-300 text-base-content/80 px-2 py-1 rounded">
                                {formatProviderAttribution(terraActivityData.user?.provider, terraActivityData.user?.deviceModel)}
                              </span>
                            )}
                          </div>

                          {/* Minimal Route Map */}
                          <div className="rounded-lg p-3">
                            <div className="relative h-48 overflow-hidden">
                              {(() => {
                                const samples =
                                  terraActivityData.position_data
                                    ?.position_samples || [];
                                const validSamples = samples.filter(
                                  (s) =>
                                    s.coords_lat_lng_deg &&
                                    s.coords_lat_lng_deg.length === 2 &&
                                    !isNaN(s.coords_lat_lng_deg[0]) &&
                                    !isNaN(s.coords_lat_lng_deg[1]),
                                );

                                if (validSamples.length === 0)
                                  return (
                                    <div className="text-base-content/60 text-center py-16">
                                      No valid GPS data available
                                    </div>
                                  );

                                // Calculate bounding box
                                const lats = validSamples.map(
                                  (s) => s.coords_lat_lng_deg[0],
                                );
                                const lngs = validSamples.map(
                                  (s) => s.coords_lat_lng_deg[1],
                                );
                                const minLat = Math.min(...lats);
                                const maxLat = Math.max(...lats);
                                const minLng = Math.min(...lngs);
                                const maxLng = Math.max(...lngs);

                                // Add padding to bounding box
                                const latRange = maxLat - minLat || 0.001;
                                const lngRange = maxLng - minLng || 0.001;
                                const padding = 0.1; // 10% padding
                                const paddedMinLat =
                                  minLat - latRange * padding;
                                const paddedMaxLat =
                                  maxLat + latRange * padding;
                                const paddedMinLng =
                                  minLng - lngRange * padding;
                                const paddedMaxLng =
                                  maxLng + lngRange * padding;

                                // Calculate distance and pace from GPS data
                                const calculateDistance = (
                                  samples: typeof validSamples,
                                ): number => {
                                  if (samples.length < 2) return 0;

                                  let totalDistance = 0;
                                  for (let i = 1; i < samples.length; i++) {
                                    const [lat1, lng1] =
                                      samples[i - 1].coords_lat_lng_deg;
                                    const [lat2, lng2] =
                                      samples[i].coords_lat_lng_deg;

                                    // Haversine formula for distance between two GPS points
                                    const R = 6371000; // Earth's radius in meters
                                    const dLat =
                                      ((lat2 - lat1) * Math.PI) / 180;
                                    const dLng =
                                      ((lng2 - lng1) * Math.PI) / 180;
                                    const a =
                                      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                                      Math.cos((lat1 * Math.PI) / 180) *
                                        Math.cos((lat2 * Math.PI) / 180) *
                                        Math.sin(dLng / 2) *
                                        Math.sin(dLng / 2);
                                    const c =
                                      2 *
                                      Math.atan2(
                                        Math.sqrt(a),
                                        Math.sqrt(1 - a),
                                      );
                                    totalDistance += R * c;
                                  }
                                  return totalDistance; // in meters
                                };

                                const totalDistanceMeters =
                                  calculateDistance(validSamples);
                                const totalDistanceKm =
                                  totalDistanceMeters / 1000;

                                // Calculate pace if we have activity duration
                                const activityDurationMinutes =
                                  activityLog?.duration || 0;
                                const paceMinutesPerKm =
                                  totalDistanceKm > 0
                                    ? activityDurationMinutes / totalDistanceKm
                                    : 0;
                                const paceMinutes =
                                  Math.floor(paceMinutesPerKm);
                                const paceSeconds = Math.round(
                                  (paceMinutesPerKm - paceMinutes) * 60,
                                );

                                // Calculate proper aspect ratio to avoid squashing
                                // Convert GPS degrees to approximate meters for aspect ratio calculation
                                const avgLat = (minLat + maxLat) / 2;
                                const latMetersPerDegree = 111000; // roughly 111 km per degree latitude
                                const lngMetersPerDegree =
                                  111000 * Math.cos((avgLat * Math.PI) / 180); // longitude varies with latitude

                                const latRangeMeters =
                                  (paddedMaxLat - paddedMinLat) *
                                  latMetersPerDegree;
                                const lngRangeMeters =
                                  (paddedMaxLng - paddedMinLng) *
                                  lngMetersPerDegree;

                                // Calculate aspect ratio and fit properly in container
                                const containerWidth = 400;
                                const containerHeight = 200;
                                const gpsAspectRatio =
                                  lngRangeMeters / latRangeMeters;
                                const containerAspectRatio =
                                  containerWidth / containerHeight;

                                let viewBoxWidth = containerWidth;
                                let viewBoxHeight = containerHeight;
                                let offsetX = 0;
                                let offsetY = 0;

                                if (gpsAspectRatio > containerAspectRatio) {
                                  // GPS data is wider - fit to width, center vertically
                                  viewBoxHeight =
                                    containerWidth / gpsAspectRatio;
                                  offsetY =
                                    (containerHeight - viewBoxHeight) / 2;
                                } else {
                                  // GPS data is taller - fit to height, center horizontally
                                  viewBoxWidth =
                                    containerHeight * gpsAspectRatio;
                                  offsetX = (containerWidth - viewBoxWidth) / 2;
                                }

                                // Convert GPS coordinates to SVG coordinates
                                const gpxToSvg = (
                                  lat: number,
                                  lng: number,
                                ): [number, number] => {
                                  const x =
                                    offsetX +
                                    ((lng - paddedMinLng) /
                                      (paddedMaxLng - paddedMinLng)) *
                                      viewBoxWidth;
                                  const y =
                                    offsetY +
                                    ((paddedMaxLat - lat) /
                                      (paddedMaxLat - paddedMinLat)) *
                                      viewBoxHeight;
                                  return [x, y];
                                };

                                // Generate the path
                                const pathPoints = validSamples.map(
                                  (sample) => {
                                    const [x, y] = gpxToSvg(
                                      sample.coords_lat_lng_deg[0],
                                      sample.coords_lat_lng_deg[1],
                                    );
                                    return `${x.toFixed(2)},${y.toFixed(2)}`;
                                  },
                                );

                                const pathData =
                                  pathPoints.length > 0
                                    ? `M${pathPoints[0]} ${pathPoints
                                        .slice(1)
                                        .map((p) => `L${p}`)
                                        .join(" ")}`
                                    : "";

                                // Start and end points
                                const startPoint =
                                  validSamples.length > 0
                                    ? gpxToSvg(
                                        validSamples[0].coords_lat_lng_deg[0],
                                        validSamples[0].coords_lat_lng_deg[1],
                                      )
                                    : null;
                                const endPoint =
                                  validSamples.length > 1
                                    ? gpxToSvg(
                                        validSamples[validSamples.length - 1]
                                          .coords_lat_lng_deg[0],
                                        validSamples[validSamples.length - 1]
                                          .coords_lat_lng_deg[1],
                                      )
                                    : null;

                                return (
                                  <svg
                                    className="w-full h-full"
                                    viewBox={`0 0 ${containerWidth} ${containerHeight}`}
                                    preserveAspectRatio="xMidYMid meet"
                                  >
                                    {/* Background */}
                                    <rect
                                      width={containerWidth}
                                      height={containerHeight}
                                      fill="transparent"
                                    />

                                    {/* Route path */}
                                    {pathData && (
                                      <path
                                        d={pathData}
                                        stroke="#3b82f6"
                                        strokeWidth="3"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    )}

                                    {/* Start point */}
                                    {startPoint && (
                                      <circle
                                        cx={startPoint[0]}
                                        cy={startPoint[1]}
                                        r="4"
                                        fill="#22c55e"
                                        stroke="white"
                                        strokeWidth="2"
                                      />
                                    )}

                                    {/* End point */}
                                    {endPoint && startPoint && (
                                      <circle
                                        cx={endPoint[0]}
                                        cy={endPoint[1]}
                                        r="4"
                                        fill="#ef4444"
                                        stroke="white"
                                        strokeWidth="2"
                                      />
                                    )}
                                  </svg>
                                );
                              })()}
                            </div>

                            {isGarminProvider(terraActivityData.user?.provider) && (
                              <GarminAttribution
                                deviceModel={terraActivityData.user?.deviceModel}
                                className="mt-2"
                              />
                            )}

                            {/* Route metrics */}
                            {(() => {
                              const samples =
                                terraActivityData.position_data
                                  ?.position_samples || [];
                              const validSamples = samples.filter(
                                (s) =>
                                  s.coords_lat_lng_deg &&
                                  s.coords_lat_lng_deg.length === 2 &&
                                  !isNaN(s.coords_lat_lng_deg[0]) &&
                                  !isNaN(s.coords_lat_lng_deg[1]),
                              );

                              if (validSamples.length === 0) return null;

                              // Calculate distance using Haversine formula
                              let totalDistance = 0;
                              for (let i = 1; i < validSamples.length; i++) {
                                const [lat1, lng1] =
                                  validSamples[i - 1].coords_lat_lng_deg;
                                const [lat2, lng2] =
                                  validSamples[i].coords_lat_lng_deg;

                                const R = 6371000;
                                const dLat = ((lat2 - lat1) * Math.PI) / 180;
                                const dLng = ((lng2 - lng1) * Math.PI) / 180;
                                const a =
                                  Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                                  Math.cos((lat1 * Math.PI) / 180) *
                                    Math.cos((lat2 * Math.PI) / 180) *
                                    Math.sin(dLng / 2) *
                                    Math.sin(dLng / 2);
                                const c =
                                  2 *
                                  Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                                totalDistance += R * c;
                              }

                              const totalDistanceMeters = totalDistance;
                              const totalDistanceKm =
                                totalDistanceMeters / 1000;
                              const activityDurationMinutes =
                                activityLog?.duration || 0;
                              const paceMinutesPerKm =
                                totalDistanceKm > 0
                                  ? activityDurationMinutes / totalDistanceKm
                                  : 0;
                              const paceMinutes = Math.floor(paceMinutesPerKm);
                              const paceSeconds = Math.round(
                                (paceMinutesPerKm - paceMinutes) * 60,
                              );

                              return (
                                <div className="flex justify-between items-center mt-3 pt-3 border-t border-base-300 px-4">
                                  <div className="text-center">
                                    <div className="text-lg font-semibold text-base-content font-sans">
                                      {totalDistanceKm >= 1
                                        ? `${totalDistanceKm.toFixed(2)} km`
                                        : `${totalDistanceMeters.toFixed(0)} m`}
                                    </div>
                                    <div className="text-xs text-base-content/60">
                                      Distance
                                    </div>
                                  </div>
                                  {paceMinutesPerKm > 0 && (
                                    <div className="text-center">
                                      <div className="text-lg font-semibold text-base-content font-sans">
                                        {`${paceMinutes}:${paceSeconds
                                          .toString()
                                          .padStart(2, "0")}`}
                                      </div>
                                      <div className="text-xs text-base-content/60">
                                        Pace (min/km)
                                      </div>
                                    </div>
                                  )}
                                  <div className="text-center">
                                    <div className="text-lg font-semibold text-base-content font-sans">
                                      {formatDuration(activityDurationMinutes)}
                                    </div>
                                    <div className="text-xs text-base-content/60">
                                      Duration
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Map legend */}
                            <div className="flex justify-between text-xs text-base-content/60 mt-2">
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                <span>Start</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                <span>End</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <div className="w-3 h-0.5 bg-blue-500"></div>
                                <span>Route</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                    {/* Power Section */}
                    {terraActivityData?.power_data?.power_samples &&
                      terraActivityData.power_data.power_samples.length >
                        0 && (
                        <div className="bg-base-200 shadow-sm mt-4 mb-4 rounded-md mx-4">
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center space-x-2">
                              <h2 className="text-base text-base-content">
                                Power
                              </h2>
                              {isGarminProvider(terraActivityData.user?.provider) ? (
                                <GarminAttribution deviceModel={terraActivityData.user?.deviceModel} />
                              ) : formatProviderAttribution(terraActivityData.user?.provider, terraActivityData.user?.deviceModel) && (
                                <span className="text-xs bg-base-300 text-base-content/80 px-2 py-1 rounded">
                                  {formatProviderAttribution(terraActivityData.user?.provider, terraActivityData.user?.deviceModel)}
                                </span>
                              )}
                            </div>
                            {terraActivityData.power_data.avg_watts && (
                              <div className="text-right">
                                <div className="text-2xl font-bold text-base-content">
                                  {Math.round(
                                    terraActivityData.power_data.avg_watts,
                                  )}
                                </div>
                                <div className="text-xs text-base-content/60">
                                  watts (avg)
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="p-4 pt-0">
                            {/* Power Timeline */}
                            <div className="mb-4">
                              <div className="relative h-48">
                                <svg
                                  className="w-full h-full"
                                  viewBox="0 0 1000 200"
                                  preserveAspectRatio="none"
                                >
                                  {/* Background */}
                                  <rect
                                    width="1000"
                                    height="120"
                                    fill="transparent"
                                  />

                                  {/* Power area + line */}
                                  {(() => {
                                    const samples =
                                      terraActivityData.power_data
                                        ?.power_samples || [];
                                    const validSamples = samples.filter(
                                      (s) =>
                                        s.watts != null && s.timestamp,
                                    );

                                    if (validSamples.length === 0)
                                      return null;

                                    const minWatts = Math.min(
                                      ...validSamples.map((s) => s.watts!),
                                    );
                                    const maxWatts = Math.max(
                                      ...validSamples.map((s) => s.watts!),
                                    );
                                    const wattsRange =
                                      maxWatts - minWatts || 1;

                                    const linePoints = validSamples
                                      .map((sample, index) => {
                                        const x =
                                          (index /
                                            (validSamples.length - 1)) *
                                          1000;
                                        const y =
                                          120 -
                                          ((sample.watts! - minWatts) /
                                            wattsRange) *
                                            100 -
                                          10;
                                        return `${x},${y}`;
                                      })
                                      .join(" ");

                                    const areaPoints = `0,120 ${linePoints} 1000,120`;

                                    return (
                                      <>
                                        <polygon
                                          fill="#f59e0b"
                                          fillOpacity="0.15"
                                          points={areaPoints}
                                        />
                                        <polyline
                                          fill="none"
                                          stroke="#f59e0b"
                                          strokeWidth="2"
                                          points={linePoints}
                                        />
                                      </>
                                    );
                                  })()}

                                  {/* Y-axis labels for min/max watts */}
                                  {(() => {
                                    const samples =
                                      terraActivityData.power_data
                                        ?.power_samples || [];
                                    const validSamples = samples.filter(
                                      (s) =>
                                        s.watts != null && s.timestamp,
                                    );

                                    if (validSamples.length === 0)
                                      return null;

                                    const minWatts = Math.min(
                                      ...validSamples.map((s) => s.watts!),
                                    );
                                    const maxWatts = Math.max(
                                      ...validSamples.map((s) => s.watts!),
                                    );

                                    return (
                                      <>
                                        <text
                                          x="10"
                                          y="15"
                                          fill="#6b7280"
                                          fontSize="10"
                                          fontFamily="system-ui, sans-serif"
                                        >
                                          {Math.round(maxWatts)}W
                                        </text>
                                        <text
                                          x="10"
                                          y="110"
                                          fill="#6b7280"
                                          fontSize="10"
                                          fontFamily="system-ui, sans-serif"
                                        >
                                          {Math.round(minWatts)}W
                                        </text>
                                      </>
                                    );
                                  })()}
                                </svg>
                              </div>
                              <div className="flex justify-between text-xs text-base-content/60 mt-2">
                                <span>
                                  {terraActivityData.metadata?.start_time
                                    ? new Date(
                                        terraActivityData.metadata.start_time,
                                      ).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : "Start"}
                                </span>
                                <span>
                                  {terraActivityData.metadata?.end_time
                                    ? new Date(
                                        terraActivityData.metadata.end_time,
                                      ).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })
                                    : "End"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                    {/* Heart Rate Zones Section */}
                    {terraActivityData?.heart_rate_data && (
                      <div className="bg-base-200 shadow-sm mt-4 mb-4 rounded-md mx-4">
                        <div className="flex items-center justify-between p-4">
                          <div className="flex items-center space-x-2">
                            <h2 className="text-base text-base-content">
                              Heart Rate Zones
                            </h2>
                            {isGarminProvider(terraActivityData.user?.provider) ? (
                              <GarminAttribution deviceModel={terraActivityData.user?.deviceModel} />
                            ) : formatProviderAttribution(terraActivityData.user?.provider, terraActivityData.user?.deviceModel) && (
                              <span className="text-xs bg-base-300 text-base-content/80 px-2 py-1 rounded">
                                {formatProviderAttribution(terraActivityData.user?.provider, terraActivityData.user?.deviceModel)}
                              </span>
                            )}
                          </div>
                          {terraActivityData.heart_rate_data.summary
                            ?.avg_hr_bpm && (
                            <div className="text-right">
                              <div className="text-2xl font-bold text-base-content">
                                {Math.round(
                                  terraActivityData.heart_rate_data.summary
                                    .avg_hr_bpm,
                                )}
                              </div>
                              <div className="text-xs text-base-content/60">
                                bpm (avg)
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="p-4 pt-0">
                          {/* Heart Rate Timeline */}
                          {terraActivityData.heart_rate_data.detailed
                            ?.hr_samples &&
                            terraActivityData.heart_rate_data.detailed
                              .hr_samples.length > 0 && (
                              <div className="mb-4">
                                <div className="">
                                  <div className="relative h-48">
                                    <svg
                                      className="w-full h-full"
                                      viewBox="0 0 1000 200"
                                      preserveAspectRatio="none"
                                    >
                                      {/* Background */}
                                      <rect
                                        width="1000"
                                        height="120"
                                        fill="transparent"
                                      />

                                      {/* Heart rate zones as background bands */}
                                      {(() => {
                                        const zones =
                                          terraActivityData.heart_rate_data
                                            ?.summary?.hr_zone_data || [];

                                        // Map zone numbers to colors consistently (same logic as progress bars)
                                        const getZoneColor = (
                                          zoneNumber: number | undefined,
                                          zoneName: string | undefined,
                                          index: number,
                                        ) => {
                                          // Try to match by zone number first
                                          switch (zoneNumber) {
                                            case 4:
                                              return "#ef4444"; // Peak - Red
                                            case 3:
                                              return "#f59e0b"; // Vigorous - Orange
                                            case 2:
                                              return "#10b981"; // Moderate - Green
                                            case 1:
                                              return "#3b82f6"; // Light - Blue
                                            case 0:
                                              return "#3b82f6"; // Light - Blue (some systems use 0-based)
                                          }

                                          // Fallback to zone name matching
                                          const lowerName = (
                                            zoneName || ""
                                          ).toLowerCase();
                                          if (
                                            lowerName.includes("peak") ||
                                            lowerName.includes("anaerobic") ||
                                            lowerName.includes("vo2")
                                          ) {
                                            return "#ef4444"; // Red
                                          }
                                          if (
                                            lowerName.includes("vigorous") ||
                                            lowerName.includes("threshold") ||
                                            lowerName.includes("lactate")
                                          ) {
                                            return "#f59e0b"; // Orange
                                          }
                                          if (
                                            lowerName.includes("moderate") ||
                                            lowerName.includes("aerobic") ||
                                            lowerName.includes("tempo")
                                          ) {
                                            return "#10b981"; // Green
                                          }
                                          if (
                                            lowerName.includes("light") ||
                                            lowerName.includes("easy") ||
                                            lowerName.includes("recovery")
                                          ) {
                                            return "#3b82f6"; // Blue
                                          }

                                          // Final fallback - assign colors by sorted index for consistency
                                          const colors = [
                                            "#ef4444",
                                            "#f59e0b",
                                            "#10b981",
                                            "#3b82f6",
                                          ]; // Red, Orange, Green, Blue
                                          return (
                                            colors[index % colors.length] ||
                                            "#6b7280"
                                          );
                                        };

                                        return zones.map((zone, index) => {
                                          if (
                                            !zone.start_percentage ||
                                            !zone.end_percentage
                                          )
                                            return null;
                                          const startY =
                                            (100 - zone.end_percentage) * 1.2;
                                          const endY =
                                            (100 - zone.start_percentage) * 1.2;
                                          const height = endY - startY;

                                          return (
                                            <rect
                                              key={index}
                                              x="0"
                                              y={startY}
                                              width="1000"
                                              height={height}
                                              fill={getZoneColor(
                                                zone.zone,
                                                zone.name,
                                                index,
                                              )}
                                              opacity="0.1"
                                            />
                                          );
                                        });
                                      })()}

                                      {/* Heart rate line */}
                                      {(() => {
                                        const samples =
                                          terraActivityData.heart_rate_data
                                            ?.detailed?.hr_samples || [];
                                        const validSamples = samples.filter(
                                          (s) => s.bpm && s.timestamp,
                                        );

                                        if (validSamples.length === 0)
                                          return null;

                                        const minHR = Math.min(
                                          ...validSamples.map((s) => s.bpm!),
                                        );
                                        const maxHR = Math.max(
                                          ...validSamples.map((s) => s.bpm!),
                                        );
                                        const hrRange = maxHR - minHR || 1;

                                        const points = validSamples
                                          .map((sample, index) => {
                                            const x =
                                              (index /
                                                (validSamples.length - 1)) *
                                              1000;
                                            const y =
                                              120 -
                                              ((sample.bpm! - minHR) /
                                                hrRange) *
                                                100 -
                                              10;
                                            return `${x},${y}`;
                                          })
                                          .join(" ");

                                        return (
                                          <polyline
                                            fill="none"
                                            stroke="#374151"
                                            strokeWidth="2"
                                            points={points}
                                          />
                                        );
                                      })()}

                                      {/* HR threshold lines */}
                                      {terraActivityData.heart_rate_data.summary?.hr_zone_data?.map(
                                        (zone, index) => {
                                          if (!zone.end_percentage) return null;
                                          const y =
                                            (100 - zone.end_percentage) * 1.2;

                                          // Map zone numbers to colors consistently (same logic as progress bars)
                                          const getZoneColor = (
                                            zoneNumber: number | undefined,
                                            zoneName: string | undefined,
                                            index: number,
                                          ) => {
                                            // Try to match by zone number first
                                            switch (zoneNumber) {
                                              case 4:
                                                return "#ef4444"; // Peak - Red
                                              case 3:
                                                return "#f59e0b"; // Vigorous - Orange
                                              case 2:
                                                return "#10b981"; // Moderate - Green
                                              case 1:
                                                return "#3b82f6"; // Light - Blue
                                              case 0:
                                                return "#3b82f6"; // Light - Blue (some systems use 0-based)
                                            }

                                            // Fallback to zone name matching
                                            const lowerName = (
                                              zoneName || ""
                                            ).toLowerCase();
                                            if (
                                              lowerName.includes("peak") ||
                                              lowerName.includes("anaerobic") ||
                                              lowerName.includes("vo2")
                                            ) {
                                              return "#ef4444"; // Red
                                            }
                                            if (
                                              lowerName.includes("vigorous") ||
                                              lowerName.includes("threshold") ||
                                              lowerName.includes("lactate")
                                            ) {
                                              return "#f59e0b"; // Orange
                                            }
                                            if (
                                              lowerName.includes("moderate") ||
                                              lowerName.includes("aerobic") ||
                                              lowerName.includes("tempo")
                                            ) {
                                              return "#10b981"; // Green
                                            }
                                            if (
                                              lowerName.includes("light") ||
                                              lowerName.includes("easy") ||
                                              lowerName.includes("recovery")
                                            ) {
                                              return "#3b82f6"; // Blue
                                            }

                                            // Final fallback - assign colors by sorted index for consistency
                                            const colors = [
                                              "#ef4444",
                                              "#f59e0b",
                                              "#10b981",
                                              "#3b82f6",
                                            ]; // Red, Orange, Green, Blue
                                            return (
                                              colors[index % colors.length] ||
                                              "#6b7280"
                                            );
                                          };

                                          return (
                                            <line
                                              key={index}
                                              x1="0"
                                              y1={y}
                                              x2="1000"
                                              y2={y}
                                              stroke={getZoneColor(
                                                zone.zone,
                                                zone.name,
                                                index,
                                              )}
                                              strokeWidth="1"
                                              strokeDasharray="4,4"
                                              opacity="0.5"
                                            />
                                          );
                                        },
                                      )}

                                      {/* Session Measurement Markers */}
                                      {session?.ActivityMeasurementList &&
                                        terraActivityData.metadata
                                          ?.start_time &&
                                        terraActivityData.metadata?.end_time &&
                                        (() => {
                                          const startTime = new Date(
                                            terraActivityData.metadata
                                              .start_time,
                                          ).getTime();
                                          const endTime = new Date(
                                            terraActivityData.metadata.end_time,
                                          ).getTime();
                                          const timeRange = endTime - startTime;

                                          if (timeRange <= 0) return null;

                                          return session.ActivityMeasurementList.filter(
                                            (m) => m.timeLogged,
                                          ).map((measurement, index) => {
                                            const logTime = new Date(
                                              measurement.timeLogged!,
                                            ).getTime();
                                            const x =
                                              ((logTime - startTime) /
                                                timeRange) *
                                              1000;

                                            // Skip if outside the time range
                                            if (x < 0 || x > 1000) return null;

                                            const activityName =
                                              getActivityTypeName(measurement);

                                            return (
                                              <g key={`measurement-${index}`}>
                                                {/* Vertical line */}
                                                <line
                                                  x1={x}
                                                  y1="0"
                                                  x2={x}
                                                  y2="120"
                                                  stroke="#22c55e"
                                                  strokeWidth="2"
                                                  opacity="0.7"
                                                />
                                                {/* Label text */}
                                                <text
                                                  x={x}
                                                  y="145"
                                                  fill="#000000"
                                                  fontSize="16"
                                                  fontFamily="system-ui, sans-serif"
                                                  fontWeight="700"
                                                  textAnchor="start"
                                                  transform={`rotate(-90, ${x}, 145)`}
                                                >
                                                  {activityName.length > 15
                                                    ? activityName.substring(
                                                        0,
                                                        15,
                                                      ) + "..."
                                                    : activityName}
                                                </text>
                                              </g>
                                            );
                                          });
                                        })()}

                                      {/* Y-axis labels for min/max HR */}
                                      {(() => {
                                        const samples =
                                          terraActivityData.heart_rate_data
                                            ?.detailed?.hr_samples || [];
                                        const validSamples = samples.filter(
                                          (s) => s.bpm && s.timestamp,
                                        );

                                        if (validSamples.length === 0)
                                          return null;

                                        const minHR = Math.min(
                                          ...validSamples.map((s) => s.bpm!),
                                        );
                                        const maxHR = Math.max(
                                          ...validSamples.map((s) => s.bpm!),
                                        );

                                        return (
                                          <>
                                            {/* Max HR label at top */}
                                            <text
                                              x="10"
                                              y="15"
                                              fill="#6b7280"
                                              fontSize="10"
                                              fontFamily="system-ui, sans-serif"
                                            >
                                              {maxHR}
                                            </text>

                                            {/* Min HR label at bottom */}
                                            <text
                                              x="10"
                                              y="110"
                                              fill="#6b7280"
                                              fontSize="10"
                                              fontFamily="system-ui, sans-serif"
                                            >
                                              {minHR}
                                            </text>
                                          </>
                                        );
                                      })()}
                                    </svg>
                                  </div>
                                  <div className="flex justify-between text-xs text-base-content/60 mt-2">
                                    <span>
                                      {terraActivityData.metadata?.start_time
                                        ? new Date(
                                            terraActivityData.metadata
                                              .start_time,
                                          ).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })
                                        : "Start"}
                                    </span>
                                    <span>
                                      {terraActivityData.metadata?.end_time
                                        ? new Date(
                                            terraActivityData.metadata.end_time,
                                          ).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })
                                        : "End"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}

                          {/* Radial Heart Rate Timeline */}
                          {terraActivityData.heart_rate_data.detailed
                            ?.hr_samples &&
                            terraActivityData.heart_rate_data.detailed
                              .hr_samples.length > 1 &&
                            terraActivityData.metadata?.start_time &&
                            terraActivityData.metadata?.end_time && (
                              <div className="mb-4 rounded-md bg-base-100 p-3 mt-8">
                                <div className="mb-2 flex justify-between">
                                  <p>
                                    <span className="bg-lime p-1 px-2 rounded-full text-xs">
                                      <strong>beta</strong> visualisation
                                    </span>
                                  </p>
                                  <button
                                    onClick={startRadialPlayback}
                                    className="rounded-md border border-base-300 bg-base-100 px-3 py-1 text-xs font-medium text-base-content/80 hover:bg-base-200 transition-colors"
                                  >
                                    {isRadialPlaying
                                      ? "Playing..."
                                      : "Play 10s Replay"}
                                  </button>
                                </div>
                                {(() => {
                                  const rawSamples =
                                    terraActivityData.heart_rate_data?.detailed
                                      ?.hr_samples || [];
                                  const validSamples = rawSamples
                                    .filter((s) => s.bpm && s.timestamp)
                                    .map((s) => ({
                                      bpm: s.bpm as number,
                                      time: new Date(
                                        s.timestamp as string,
                                      ).getTime(),
                                    }))
                                    .filter((s) => !Number.isNaN(s.time))
                                    .sort((a, b) => a.time - b.time);

                                  const sessionStart = new Date(
                                    terraActivityData.metadata!.start_time!,
                                  ).getTime();
                                  const sessionEnd = new Date(
                                    terraActivityData.metadata!.end_time!,
                                  ).getTime();
                                  const durationMs = sessionEnd - sessionStart;
                                  const playbackCutoffTime =
                                    sessionStart +
                                    durationMs * radialPlaybackProgress;
                                  const labelLeadMs = durationMs * 0.06;

                                  if (
                                    validSamples.length < 2 ||
                                    !Number.isFinite(durationMs) ||
                                    durationMs <= 0
                                  ) {
                                    return (
                                      <div className="text-xs text-base-content/60">
                                        Not enough data for radial timeline.
                                      </div>
                                    );
                                  }

                                  const minHR = Math.min(
                                    ...validSamples.map((s) => s.bpm),
                                  );
                                  const maxHR = Math.max(
                                    ...validSamples.map((s) => s.bpm),
                                  );
                                  const hrRange = maxHR - minHR || 1;

                                  const size = 360;
                                  const center = size / 2;
                                  const innerRadius = 48;
                                  const outerRadius = 136;
                                  const labelPadding = 12;

                                  const clamp = (value: number) =>
                                    Math.max(0, Math.min(1, value));

                                  const toAngle = (timeMs: number) =>
                                    -Math.PI / 2 +
                                    clamp(
                                      (timeMs - sessionStart) / durationMs,
                                    ) *
                                      Math.PI *
                                      2;

                                  const toPoint = (
                                    timeMs: number,
                                    bpm: number,
                                  ) => {
                                    const angle = toAngle(timeMs);
                                    const normalized = (bpm - minHR) / hrRange;
                                    const radius =
                                      innerRadius +
                                      normalized * (outerRadius - innerRadius);
                                    return {
                                      x: center + radius * Math.cos(angle),
                                      y: center + radius * Math.sin(angle),
                                    };
                                  };

                                  const visibleSamples = validSamples.filter(
                                    (sample) =>
                                      sample.time <= playbackCutoffTime,
                                  );

                                  const radialPath =
                                    visibleSamples.length > 1
                                      ? visibleSamples
                                          .map((sample, index) => {
                                            const { x, y } = toPoint(
                                              sample.time,
                                              sample.bpm,
                                            );
                                            return `${index === 0 ? "M" : "L"}${x.toFixed(
                                              2,
                                            )},${y.toFixed(2)}`;
                                          })
                                          .join(" ")
                                      : "";

                                  let filledAreaPath = "";
                                  if (visibleSamples.length > 1) {
                                    const firstSample = visibleSamples[0];
                                    const lastSample =
                                      visibleSamples[visibleSamples.length - 1];
                                    const firstAngle = toAngle(
                                      firstSample.time,
                                    );
                                    const lastAngle = toAngle(lastSample.time);
                                    const startInnerX =
                                      center +
                                      innerRadius * Math.cos(firstAngle);
                                    const startInnerY =
                                      center +
                                      innerRadius * Math.sin(firstAngle);
                                    const endInnerX =
                                      center +
                                      innerRadius * Math.cos(lastAngle);
                                    const endInnerY =
                                      center +
                                      innerRadius * Math.sin(lastAngle);
                                    const angleDelta = Math.abs(
                                      lastAngle - firstAngle,
                                    );
                                    const largeArcFlag =
                                      angleDelta > Math.PI ? 1 : 0;
                                    filledAreaPath = `${radialPath} L${endInnerX.toFixed(
                                      2,
                                    )},${endInnerY.toFixed(
                                      2,
                                    )} A${innerRadius},${innerRadius} 0 ${largeArcFlag} 0 ${startInnerX.toFixed(
                                      2,
                                    )},${startInnerY.toFixed(2)} Z`;
                                  }

                                  const activityPalette = [
                                    "#2563eb",
                                    "#16a34a",
                                    "#9333ea",
                                    "#ea580c",
                                    "#0891b2",
                                    "#dc2626",
                                    "#ca8a04",
                                  ];

                                  const getActivityColor = (name: string) => {
                                    let hash = 0;
                                    for (let i = 0; i < name.length; i++) {
                                      hash =
                                        (hash * 31 + name.charCodeAt(i)) >>> 0;
                                    }
                                    return activityPalette[
                                      hash % activityPalette.length
                                    ];
                                  };

                                  const measurements = (
                                    session?.ActivityMeasurementList || []
                                  )
                                    .filter((m) => m.timeLogged)
                                    .map((m) => {
                                      const time = new Date(
                                        m.timeLogged as string,
                                      ).getTime();
                                      const name = getActivityTypeName(m);
                                      return {
                                        time,
                                        name,
                                      };
                                    })
                                    .filter(
                                      (m) =>
                                        !Number.isNaN(m.time) &&
                                        m.time >= sessionStart &&
                                        m.time <= sessionEnd,
                                    )
                                    .sort((a, b) => a.time - b.time);

                                  // Keep all ticks, but thin labels so names stay readable.
                                  const minLabelGapMs = durationMs * 0.05;
                                  const labelMeasurements: Array<{
                                    time: number;
                                    name: string;
                                  }> = [];
                                  measurements.forEach((measurement) => {
                                    const prev =
                                      labelMeasurements[
                                        labelMeasurements.length - 1
                                      ];
                                    if (
                                      !prev ||
                                      measurement.time - prev.time >=
                                        minLabelGapMs ||
                                      measurement.name !== prev.name
                                    ) {
                                      labelMeasurements.push(measurement);
                                    }
                                  });
                                  const visibleLabelMeasurements =
                                    labelMeasurements.filter(
                                      (measurement) =>
                                        measurement.time <=
                                        playbackCutoffTime + labelLeadMs,
                                    );

                                  return (
                                    <div>
                                      <div className="flex justify-center">
                                        <svg
                                          className="w-full h-auto overflow-visible"
                                          viewBox={`${-labelPadding} ${-labelPadding} ${
                                            size + labelPadding * 2
                                          } ${size + labelPadding * 2}`}
                                        >
                                          {/* Filled area under HR line toward center */}
                                          {filledAreaPath && (
                                            <path
                                              d={filledAreaPath}
                                              fill="#111827"
                                              opacity="0.12"
                                            />
                                          )}

                                          {/* Heart rate path */}
                                          {radialPath && (
                                            <path
                                              d={radialPath}
                                              fill="none"
                                              stroke="#111827"
                                              strokeWidth="2.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          )}

                                          {/* Activity labels (thinned) */}
                                          {visibleLabelMeasurements.map(
                                            (m, index) => {
                                              const angle = toAngle(m.time);
                                              const labelR =
                                                100 + (index % 2) * 10;
                                              const textX =
                                                center +
                                                labelR * Math.cos(angle);
                                              const textY =
                                                center +
                                                labelR * Math.sin(angle);
                                              const color = getActivityColor(
                                                m.name,
                                              );
                                              const radialDegrees =
                                                (angle * 180) / Math.PI;
                                              const normalizedDegrees =
                                                (radialDegrees + 360) % 360;
                                              const readableRotation =
                                                normalizedDegrees > 90 &&
                                                normalizedDegrees < 270
                                                  ? radialDegrees + 180
                                                  : radialDegrees;
                                              const truncatedName =
                                                m.name.length > 14
                                                  ? `${m.name.slice(0, 14)}...`
                                                  : m.name;

                                              return (
                                                <g
                                                  key={`activity-label-${index}`}
                                                >
                                                  <text
                                                    x={textX}
                                                    y={textY}
                                                    fill={color}
                                                    fontSize="10"
                                                    fontWeight="600"
                                                    textAnchor="middle"
                                                    dominantBaseline="middle"
                                                    transform={`rotate(${readableRotation}, ${textX}, ${textY})`}
                                                  >
                                                    {truncatedName}
                                                  </text>
                                                </g>
                                              );
                                            },
                                          )}

                                          {/* Center labels */}
                                          <circle
                                            cx={center}
                                            cy={center}
                                            r={innerRadius - 2}
                                            fill="white"
                                            stroke="#e5e7eb"
                                          />
                                          <image
                                            href={peakLogo}
                                            x={center - 20}
                                            y={center - 17}
                                            width="40"
                                            height="12"
                                            preserveAspectRatio="xMidYMid meet"
                                          />
                                          <text
                                            x={center}
                                            y={center + 14}
                                            textAnchor="middle"
                                            fill="#6b7280"
                                            fontSize="10"
                                          >
                                            {`${Math.round(minHR)}-${Math.round(maxHR)} bpm`}
                                          </text>
                                        </svg>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}

                          {/* Heart Rate Zones */}
                          {terraActivityData.heart_rate_data.summary
                            ?.hr_zone_data &&
                            terraActivityData.heart_rate_data.summary
                              .hr_zone_data.length > 0 && (
                              <div>
                                <h3 className="text-sm font-medium text-base-content mb-3">
                                  Time in each zone
                                </h3>
                                <div className="space-y-2">
                                  {terraActivityData.heart_rate_data.summary.hr_zone_data
                                    .filter(
                                      (zone) =>
                                        zone.duration_seconds &&
                                        zone.duration_seconds > 0,
                                    )
                                    .sort(
                                      (a, b) => (b.zone || 0) - (a.zone || 0),
                                    ) // Sort by zone number descending (Peak first)
                                    .map((zone, sortedIndex) => {
                                      const totalDuration =
                                        terraActivityData.heart_rate_data?.summary?.hr_zone_data?.reduce(
                                          (sum, z) =>
                                            sum + (z.duration_seconds || 0),
                                          0,
                                        ) || 1;
                                      const percentage = Math.round(
                                        ((zone.duration_seconds || 0) /
                                          totalDuration) *
                                          100,
                                      );
                                      const duration = formatDuration(
                                        (zone.duration_seconds || 0) / 60,
                                      );

                                      // Map zone numbers to colors and names consistently
                                      const getZoneColorAndName = (
                                        zoneNumber: number | undefined,
                                        zoneName: string | undefined,
                                      ) => {
                                        // Try to match by zone number first
                                        switch (zoneNumber) {
                                          case 4:
                                            return {
                                              color: "#ef4444",
                                              name: "Peak",
                                            }; // Red
                                          case 3:
                                            return {
                                              color: "#f59e0b",
                                              name: "Vigorous",
                                            }; // Orange
                                          case 2:
                                            return {
                                              color: "#10b981",
                                              name: "Moderate",
                                            }; // Green
                                          case 1:
                                            return {
                                              color: "#3b82f6",
                                              name: "Light",
                                            }; // Blue
                                          case 0:
                                            return {
                                              color: "#3b82f6",
                                              name: "Light",
                                            }; // Blue (some systems use 0-based)
                                        }

                                        // Fallback to zone name matching
                                        const lowerName = (
                                          zoneName || ""
                                        ).toLowerCase();
                                        if (
                                          lowerName.includes("peak") ||
                                          lowerName.includes("anaerobic") ||
                                          lowerName.includes("vo2") ||
                                          lowerName.includes("neuromuscular")
                                        ) {
                                          return {
                                            color: "#ef4444",
                                            name: "Peak",
                                          }; // Red
                                        }
                                        if (
                                          lowerName.includes("vigorous") ||
                                          lowerName.includes("threshold") ||
                                          lowerName.includes("lactate")
                                        ) {
                                          return {
                                            color: "#f59e0b",
                                            name: "Vigorous",
                                          }; // Orange
                                        }
                                        if (
                                          lowerName.includes("moderate") ||
                                          lowerName.includes("aerobic") ||
                                          lowerName.includes("tempo")
                                        ) {
                                          return {
                                            color: "#10b981",
                                            name: "Moderate",
                                          }; // Green
                                        }
                                        if (
                                          lowerName.includes("light") ||
                                          lowerName.includes("easy") ||
                                          lowerName.includes("recovery")
                                        ) {
                                          return {
                                            color: "#3b82f6",
                                            name: "Light",
                                          }; // Blue
                                        }

                                        // Final fallback - assign colors by sorted index for consistency
                                        const allZones =
                                          terraActivityData.heart_rate_data
                                            ?.summary?.hr_zone_data || [];
                                        const sortedZones = [...allZones].sort(
                                          (a, b) =>
                                            (b.zone || 0) - (a.zone || 0),
                                        );
                                        const zoneIndex = sortedZones.findIndex(
                                          (z) => z === zone,
                                        );
                                        const colors = [
                                          "#ef4444",
                                          "#f59e0b",
                                          "#10b981",
                                          "#3b82f6",
                                        ]; // Red, Orange, Green, Blue
                                        const names = [
                                          "Peak",
                                          "Vigorous",
                                          "Moderate",
                                          "Light",
                                        ];

                                        return {
                                          color:
                                            colors[zoneIndex % colors.length] ||
                                            "#6b7280",
                                          name:
                                            names[zoneIndex % names.length] ||
                                            `Zone ${zoneNumber || "Unknown"}`,
                                        };
                                      };

                                      const { color, name } =
                                        getZoneColorAndName(
                                          zone.zone,
                                          zone.name,
                                        );

                                      return (
                                        <div
                                          key={`${zone.zone}-${sortedIndex}`}
                                          className="flex items-center space-x-3"
                                        >
                                          <div className="flex items-center space-x-2 w-24">
                                            <div
                                              className="w-3 h-3 rounded-full"
                                              style={{ backgroundColor: color }}
                                            />
                                            <span className="text-sm text-base-content/80">
                                              {zone.name || name}
                                            </span>
                                          </div>
                                          <div className="flex-1 flex items-center space-x-3">
                                            <div className="text-xs text-base-content/70 w-8">
                                              {percentage}%
                                            </div>
                                            <div className="flex-1 bg-base-300 rounded-full h-2">
                                              <div
                                                className="h-2 rounded-full"
                                                style={{
                                                  backgroundColor: color,
                                                  width: `${percentage}%`,
                                                }}
                                              />
                                            </div>
                                            <div className="text-xs text-base-content/70 w-12">
                                              {duration}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            )}

                          {/* Additional Terra Metrics */}
                          {(terraActivityData.calories_data
                            ?.total_burned_calories ||
                            terraActivityData.work_data?.work_kilojoules ||
                            terraActivityData.heart_rate_data.summary
                              ?.avg_hrv_rmssd ||
                            terraActivityData.heart_rate_data.summary
                              ?.avg_hrv_sdnn) && (
                            <div className="mt-3 pt-3 border-t border-base-300">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {terraActivityData.calories_data
                                  ?.total_burned_calories && (
                                  <div className="text-center">
                                    <div className="text-xs text-base-content/60">
                                      Total Calories
                                    </div>
                                    <div className="text-sm font-medium text-base-content">
                                      {Math.round(
                                        terraActivityData.calories_data
                                          .total_burned_calories,
                                      )}
                                    </div>
                                  </div>
                                )}
                                {terraActivityData.work_data
                                  ?.work_kilojoules && (
                                  <div className="text-center">
                                    <div className="text-xs text-base-content/60">
                                      Work Output
                                    </div>
                                    <div className="text-sm font-medium text-base-content">
                                      {Math.round(
                                        terraActivityData.work_data
                                          .work_kilojoules,
                                      )}{" "}
                                      kJ
                                    </div>
                                  </div>
                                )}
                                {terraActivityData.heart_rate_data.summary
                                  ?.avg_hrv_rmssd && (
                                  <div className="text-center">
                                    <div className="text-xs text-base-content/60">
                                      HRV (RMSSD)
                                    </div>
                                    <div className="text-sm font-medium text-base-content">
                                      {Math.round(
                                        terraActivityData.heart_rate_data
                                          .summary.avg_hrv_rmssd,
                                      )}{" "}
                                      ms
                                    </div>
                                  </div>
                                )}
                                {terraActivityData.heart_rate_data.summary
                                  ?.avg_hrv_sdnn && (
                                  <div className="text-center">
                                    <div className="text-xs text-base-content/60">
                                      HRV (SDNN)
                                    </div>
                                    <div className="text-sm font-medium text-base-content">
                                      {Math.round(
                                        terraActivityData.heart_rate_data
                                          .summary.avg_hrv_sdnn,
                                      )}{" "}
                                      ms
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
          {/* Session Info */}
          <div className="py-4 px-10 pb-6">
            <div className="flex items-center space-x-2 text-xs text-base-content/60">
              <ClockIcon className="h-3 w-3" />
              <span>Logged at {formatTime(trainingLog.timestamp)}</span>
            </div>
          </div>
        </div>

        {/* Create Post Button - Desktop (show if no post or if linked post was deleted) */}
        {(!trainingLog.linkedPostID || !postExists) && (
          <div className="max-w-4xl mx-auto px-12 pb-4 hidden sm:block">
            <button
              onClick={() => setShowCreatePost(true)}
              className="flex justify-center w-full items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              title="Share training as post"
            >
              <p className="text-sm font-grotesk font-bold">Create Post</p>
            </button>
          </div>
        )}
      </div>

      {/* Export Dialog */}
      {trainingLog && (
        <SummaryExportDialog
          isOpen={exportOpen}
          onClose={() => setExportOpen(false)}
          trainingLog={{ ...trainingLog, id: trainingLogId as string }}
          activity={
            activityLog
              ? {
                  ...activityLog,
                  id: activityLog.linkedTrainingLogID || "activity",
                }
              : undefined
          }
          peakScore={dayPost?.peakScoreLog?.peakScore}
        />
      )}

      {/* Edit: performance training logs re-open the Performance flow
          (PerformanceLog.md §4 "Edit"); plain training logs use the form. */}
      {trainingLog && trainingLogId && trainingLog.performanceType && (
        <PerformanceFlow
          open={showEditForm}
          onClose={handleCloseEditForm}
          dateID={
            trainingLog.dateID ||
            generateDateID(new Date(trainingLog.timestamp))
          }
          editMode
          initialTrainingLog={{ ...trainingLog, id: trainingLogId }}
          onSaved={async () => {
            setShowEditForm(false);
            await fetchData();
          }}
        />
      )}
      {trainingLog && !trainingLog.performanceType && (
        <TrainingLogForm
          open={showEditForm}
          onClose={handleCloseEditForm}
          context={{
            dateID:
              trainingLog.dateID ||
              generateDateID(new Date(trainingLog.timestamp)),
          }}
          editMode
          logId={trainingLogId}
          initialData={trainingLog}
          onSaved={async () => {
            setShowEditForm(false);
            await fetchData();
          }}
        />
      )}

      {/* Delete confirmation (decision 5: asks about linked sessions and
          activity data when the log has any). */}
      <DeleteTrainingLogDialog
        open={showDeleteConfirm}
        log={trainingLog}
        linkedResultCount={linkedResultCount}
        isDeleting={isDeleting}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
      />

      {/* Unlink one session from this log */}
      <ConfirmDialog
        open={!!unlinkingSession}
        title="Unlink session?"
        message={`"${
          unlinkingSession?.name?.trim() || "This session"
        }" will be removed from this training log and return to "Sessions to log". Its activities are kept.`}
        confirmLabel="Unlink"
        loading={isUnlinking}
        onConfirm={handleConfirmUnlink}
        onCancel={() => setUnlinkingSession(null)}
      />

      {/* Create Post Dialog */}
      {trainingLog && currentUser && userData && (
        <CreatePostDialog
          isOpen={showCreatePost}
          onClose={async () => {
            setShowCreatePost(false);
            // Refresh data to show the new post badge
            await fetchData();
          }}
          userId={currentUser.uid}
          username={userData.username}
          userImageUrl={userData.imageUrl || ""}
          trainingLogData={{
            trainingLogId: trainingLogId || "",
            sessionId: getPrimarySessionID(trainingLog),
            notes: trainingLog.notes,
            duration: trainingLog.duration,
            load: trainingLog.load,
            averageHR: activityLog?.aveHR,
            activityLogId: trainingLog.linkedActivityLogID,
            performanceLogId: undefined,
          }}
        />
      )}

      {/* Fixed Create Post Button - Mobile (show if no post or if linked post was deleted) */}
      {(!trainingLog.linkedPostID || !postExists) && (
        <div className="fixed bottom-0 left-0 right-0 p-4 0 sm:hidden z-40 px-8 pb-10">
          <button
            onClick={() => setShowCreatePost(true)}
            className="flex justify-center w-full items-center space-x-2 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors shadow-lg"
            title="Share training as post"
          >
            <p className="text-sm font-grotesk font-bold">Create Post</p>
          </button>
        </div>
      )}
    </div>
  );
};

export default TrainingLogInsights;
